import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaTiktok } from 'react-icons/fa';
import PhysicsSocials2D from './PhysicsSocials2D';
import { servicesData } from '../data/servicesData';

const Footer = () => {
    const container = useRef(null);
    const titleRef = useRef(null);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Services', path: '/services' },
        { name: 'Work', path: '/works' },
        { name: 'Contact', path: '/contact' }
    ];

    useGSAP(() => {
        const chars = titleRef.current.querySelectorAll('.char');

        gsap.fromTo(chars,
            {
                y: 100,
                opacity: 0
            },
            {
                y: 0,
                opacity: 1,
                duration: 1,
                stagger: 0.05,
                ease: "power4.out",
                scrollTrigger: {
                    trigger: container.current,
                    start: "top 60%",
                    end: "bottom bottom",
                    toggleActions: "play none none reverse"
                }
            }
        );
    }, { scope: container });

    const text = "GET IN TOUCH";

    return (
        <footer ref={container} className="footer-section text-[#fff] pt-24 pb-4 overflow-hidden font-sans relative">
            <div className="w-full text-center mb-10 overflow-hidden py-4 px-2 relative z-10">
                <h1 ref={titleRef} className="text-[11.5vw] leading-[0.9] font-bold tracking-tighter text-[#E0E0E0] whitespace-nowrap uppercase">
                    {text.split("").map((char, index) => (
                        <span key={index} className="char inline-block">
                            {char === " " ? "\u00A0" : char}
                        </span>
                    ))}
                </h1>
            </div>

            <div className="relative w-full overflow-hidden min-h-[400px]">
                {/* Interactive 2D Physics Social Circles - High Density */}
                <PhysicsSocials2D count={40} />

                <div className="container mx-auto px-6 relative z-10">
                    {/* Content Grid */}
                    <div className="flex flex-col md:flex-row gap-8 px-6 md:px-20 mb-10 text-sm uppercase tracking-wider">
                        {/* Navigation */}
                        <div className="w-full">
                            <h4 className="text-sm border-b border-white/50 pb-1 font-bold opacity-80 mb-6 text-white">Navigation</h4>
                            <div className="flex flex-col gap-3 font-medium text-xs">
                                {navLinks.map((item) => (
                                    <Link key={item.name} to={item.path} className="hover:text-[#f04e23] text-white hover:translate-x-1 transition-all duration-300 inline-block">
                                        {item.name}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Resources/Address */}
                        <div className="w-full">
                            <h4 className="text-sm border-b border-white/50 pb-1 font-bold opacity-80 mb-6 text-white">Office</h4>
                            <div className="flex flex-col gap-1 normal-case text-xs opacity-80 leading-relaxed text-white">
                                <p>Office # 2303, Citadel</p>
                                <p>Tower Business bay, Dubai</p>
                            </div>
                        </div>

                        {/* Services List */}
                        <div className="w-full">
                            <h4 className="text-sm border-b border-white/50 pb-1 font-bold opacity-80 mb-6 text-white">Services</h4>
                            <div className="flex flex-col gap-3 font-medium text-xs">
                                {servicesData.slice(0, 5).map((service) => (
                                    <Link key={service.id} to={`/services/${service.id}`} className="hover:text-[#f04e23] text-white hover:translate-x-1 transition-all duration-300 inline-block truncate">
                                        {service.title}
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* Profile/Legal */}
                        <div className="w-full">
                            <h4 className="text-sm border-b border-white/50 pb-1 font-bold opacity-80 mb-6 text-white">Legal</h4>
                            <div className="flex flex-col gap-3 font-medium text-xs normal-case">
                                <a href="#" className="hover:text-[#f04e23] text-white transition-colors">Privacy Policy</a>
                                <a href="#" className="hover:text-[#f04e23] text-white transition-colors">Terms & Conditions</a>
                                <a href="#" className="hover:text-[#f04e23] text-white transition-colors">Cookie Policy</a>
                            </div>
                        </div>

                        {/* Social */}
                        <div className="w-full">
                            <h4 className="text-sm border-b border-white/50 pb-1 font-bold opacity-80 mb-6 text-white">Social</h4>
                            <div className="flex gap-4">
                                <a href="https://www.facebook.com/people/Xpin-Media/61581907501123/#" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#f04e23] flex items-center justify-center transition-all duration-300 group/social shadow-[0_5px_15px_-5px_rgba(240,78,35,0.4)] hover:scale-110">
                                    <FaFacebookF className="text-white" size={14} />
                                </a>
                                <a href="https://www.tiktok.com/@xpinmedia?_r=1&_t=ZS-95in72KfZ2G" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#f04e23] flex items-center justify-center transition-all duration-300 group/social shadow-[0_5px_15px_-5px_rgba(240,78,35,0.4)] hover:scale-110">
                                    <FaTiktok className="text-white" size={14} />
                                </a>
                                <a href="https://www.linkedin.com/company/xpin-media/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#f04e23] flex items-center justify-center transition-all duration-300 group/social shadow-[0_5px_15px_-5px_rgba(240,78,35,0.4)] hover:scale-110">
                                    <FaLinkedinIn className="text-white" size={14} />
                                </a>
                                <a href="https://www.instagram.com/xpinmedia/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#f04e23] flex items-center justify-center transition-all duration-300 group/social shadow-[0_5px_15px_-5px_rgba(240,78,35,0.4)] hover:scale-110">
                                    <FaInstagram className="text-white" size={14} />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Compact Glassmorphic Bottom Bar */}
                    <div className="mt-20 flex flex-row justify-between items-center gap-2 md:gap-8 relative z-10 py-6 border-t border-white/5 w-full">

                        {/* Copyright & Brand Badge */}
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 px-3 py-2 md:px-8 md:py-5 rounded-xl md:rounded-2xl group hover:bg-white/10 transition-colors duration-500">
                            <p className='text-white/80 text-[7px] md:text-[9px] uppercase font-bold tracking-[0.4em] mb-1 md:mb-2'>© {new Date().getFullYear()}</p>
                            <h2 className='text-white text-sm md:text-4xl font-black tracking-tighter uppercase leading-none'>
                                XPIN <span className="text-[#f04e23]">MEDIA</span>
                            </h2>
                        </div>

                        {/* Local Time & Scroll Badge */}
                        <div className="flex items-center gap-2 md:gap-4">
                            <div className="bg-white/5 backdrop-blur-xl border border-white/10 px-3 py-2 md:px-6 md:py-4 rounded-xl md:rounded-2xl flex flex-col items-center group hover:bg-white/10 transition-colors duration-500">
                                <span className="text-[7px] md:text-[9px] text-white/80 uppercase tracking-[0.3em] font-bold mb-0.5 md:mb-1">
                                    Time
                                </span>
                                <span className="text-[10px] md:text-2xl font-bold text-white/90 tracking-tight whitespace-nowrap">
                                    {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                </span>
                            </div>

                            <div
                                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-[#f04e23] border border-[#f04e23]/30 flex items-center justify-center text-white shadow-lg shadow-[#f04e23]/20 hover:scale-110 active:scale-95 transition-all duration-500 cursor-pointer group/up"
                            >
                                <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 md:w-6 md:h-6 transform group-hover/up:-translate-y-1 transition-transform" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
